import { useId, type MouseEvent } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { ConsoleHint } from '@/components/ui/console/ConsoleHint'
import { isNavigationActive, type NavGroup } from '@/config/navigation'

interface Props {
  item: NavGroup
  open: boolean
  expanded: boolean
  onToggle: () => void
  onReveal: () => void
  onExpand: () => void
  onSelect: (event: MouseEvent<HTMLAnchorElement>) => void
}

/** One navigation group and its submenu; disclosure is owned by the rail. */
export function IslandNavGroup({
  item,
  open,
  expanded,
  onToggle,
  onReveal,
  onExpand,
  onSelect,
}: Props) {
  const { pathname } = useLocation()
  const panelId = useId()
  const active = item.children.some((child) =>
    isNavigationActive(pathname, child.path)
  )
  const visible = open && expanded
  const Icon = item.icon
  const testId = `nav-group-${item.label.toLowerCase().replace(/\s+/g, '-')}`

  return (
    <div data-testid={testId}>
      <ConsoleHint label={item.label} side="right" disabled={open}>
        <button
          type="button"
          className="island-nav-row"
          data-active={active || undefined}
          data-testid={`${testId}-toggle`}
          aria-label={item.label}
          aria-expanded={visible}
          aria-controls={visible ? panelId : undefined}
          onClick={() => {
            if (open) onToggle()
            else {
              onReveal()
              onExpand()
            }
          }}
        >
          <span className="island-nav-icon">
            <Icon className="h-5 w-5" aria-hidden="true" />
          </span>
          <span className="island-nav-label">{item.label}</span>
          {open ? (
            <ChevronDown
              className={`island-chevron ${visible ? '' : '-rotate-90'}`}
              aria-hidden="true"
            />
          ) : (
            <span className="island-group-hint" aria-hidden="true" />
          )}
        </button>
      </ConsoleHint>
      {visible && (
        <div
          id={panelId}
          role="group"
          aria-label={`${item.label} submenu`}
          className="island-subnav"
        >
          <ul>
            {item.children.map((child) => (
              <li key={child.path}>
                <NavLink
                  to={child.path}
                  onClick={onSelect}
                  className="island-subnav-link"
                  data-testid={`nav-link-${child.path.slice(1)}`}
                >
                  <span className="island-subnav-dot" aria-hidden="true" />
                  {child.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
